import Spinner from '@components/_elements/Spinner/Spinner';
import SimplePointDataTableWithTitle from '@components/_shared/MultiColPointDataTableWithTitle';
import useGetInternalWidgetConfig from '@hooks/api/queries/widgets/useGetInternalWidgetConfig';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import { useUnitController } from '@src/contextProviders/UnitController';
import storeConnector from '@src/store/storeConnector';
import {
  WidgetTypeDeviceList,
  WidgetTypeMultiSectionPointList,
} from '@src/types/Widget';
import FormattedAlias from './Translations/FormattedAlias';

function WidgetDeviceRowDetails({
  widgetConfigId,
  selectedSourceDevice,
  unitControllerId: unitControllerIdProp,
  unitControllerEngineeringId: unitControllerEngineeringIdProp,
  onClose,
}: {
  widgetConfigId: WidgetTypeDeviceList;
  selectedSourceDevice: {
    name: string;
    id: string;
  };
  onClose: () => void;
  unitControllerId?: string;
  unitControllerEngineeringId?: string;
}) {
  const context = useUnitController();

  const unitControllerId = unitControllerIdProp ?? context.unitControllerId;
  const unitControllerEngineeringId =
    unitControllerEngineeringIdProp ?? context.unitControllerEngineeringId;

  const {
    data: widgetConfig,
    isFetching,
    isError,
  } = useGetInternalWidgetConfig<WidgetTypeMultiSectionPointList>(
    `${widgetConfigId}DetailsModal`,
    {
      sourceDeviceId: selectedSourceDevice?.id,
      ...(unitControllerId &&
        unitControllerEngineeringId && {
          unitControllerId,
          unitControllerEngineeringId,
        }),
    },
    {
      queryKeys: [selectedSourceDevice?.id, 'deviceDetails'],
      enabled: !!widgetConfigId && !!selectedSourceDevice?.id,
    },
  );

  return (
    <Dialog
      open={!!widgetConfigId && !!selectedSourceDevice?.id}
      onClose={onClose}
      maxWidth='md'
      fullWidth
    >
      {isFetching && <Spinner type='content-small' />}
      {isError && (
        <Typography textAlign='center' marginTop='50px'>
          <FormattedAlias id={'Error fetching device details.'} />
        </Typography>
      )}
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        {selectedSourceDevice?.name} <FormattedAlias id={'Details'} />
        <IconButton onClick={onClose} sx={{ mr: -2, mt: -1 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Box mt={1} mb={5}>
          {widgetConfig?.sections.map((sectionData, i) => (
            <SimplePointDataTableWithTitle
              key={`megapack-details-${i}`}
              sectionIndex={i}
              data={sectionData}
            />
          ))}
        </Box>
      </DialogContent>
    </Dialog>
  );
}

export default storeConnector(WidgetDeviceRowDetails, {
  config: ['siteMeta'],
});
